import { Button, CodeEntry, Divider, Grid, Heading, IconNames, Option, Text, usePhoneNumber, useToast } from '@afterpaytouch/core'
import clsx from 'clsx'
import { Trans, useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, ReactElement, useEffect, useRef, useState, ReactNode } from 'react'
import { FormWrapper, ProfileCard, BackLink, TransMaskedText, WelcomeBackRecognition } from '..'
import { SEND_TIMEOUT_MS } from '../../config'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import {
  CodeVerificationDirective,
  Features,
  LoginMethodTypes,
  VerificationError,
  VerificationErrorCode,
  VerificationFlow,
  VerificationFlowContext,
  VerificationFlowRoute,
  VerificationMethod,
  VerifyRegistrationSMSAuthorizationCode,
  WaitingForEvents,
} from '../../model'
import { TrackingEvent, TrackingEvents } from '../../model/monitoring'
import {
  resetCheckoutError,
  useAppDispatch,
  useAppRouter,
  useConsumerSelector,
  useCountry,
  useIsApplePaylater,
  useIsAutoRememberMeStepUp,
  useAutoRememberMeStepUpEligibility,
  useIsAutoRememberMeStepUpInFlight,
  useIsStepUpIdentityUntrusted,
  useLogLoginAttemptEvent,
  verifyRegistrationMobileEventError,
  verifyRegistrationMobileEventSuccess,
} from '../../state'
import { maskPhoneNumber } from '../../utils/phone-numbers'
import { useCheckoutSizes } from '../../utils/responsive'
import { useSet } from '../../utils/hooks'
import styles from './styles.module.css'
import { handleVerificationError, getErrorMessage, convertAuthzErrorEventToVerificationError, getErrorToastTransKey } from './helper'
import { Verifying } from './Verifying'
import { FetchVerificationWaitResponse, FetchVerificationErrorResponse, FetchVerificationSuccessResponse } from '@afterpaytouch/event-verify'
import { useProfileCardProps, useVerifyTimeout } from './hooks'
import { Description } from './Description'
import { StepUpBadge } from './StepUpBadge'
import { useFlag } from '@afterpaytouch/feature'
import { ErrorCode } from '@afterpaytouch/portal-api'

// CASHDAP-3954 (R4): minimum time the welcome-back transient stays on screen before the step-up
// OTP form — long enough to read the greeting, short enough not to delay a fast auto-send.
const STEP_UP_WELCOME_BACK_MIN_MS = 2000

interface Props {
  flow: VerificationFlow
  disabled: boolean
  onSuccess: (result?: VerificationFlowRoute) => void
  resetDisabledState: () => void
  onTerminalError: (error?: VerificationErrorCode) => void
  onComplete: () => void
  onError?: (error?: VerificationErrorCode) => void
}

interface SendCodeLinkProps {
  children: ReactNode
}

const icons: Record<VerificationMethod, IconNames> = {
  EMAIL: 'Email',
  SMS: 'Phone',
}

const trackingEvents: Record<VerificationMethod, TrackingEvents> = {
  EMAIL: 'PRESSED_VERIFY_EMAIL_BUTTON',
  SMS: 'PRESSED_VERIFY_SMS_BUTTON',
}

export const Verify: FunctionComponent<Props> = ({
  flow: {
    previousRoute,
    sendCode,
    checkCode,
    enabled,
    autoSendCode,
    allowLogout,
    revealMobile,
    initialMethod,
    context,
    sendSmsCodeEvent,
    sendSmsAuthorization,
  },
  disabled,
  onSuccess,
  resetDisabledState,
  onTerminalError,
  onComplete,
  onError = () => {},
}) => {
  const { t } = useTranslation()
  const { email: storeEmail, mobile: storeMobile, stepUpIdentity } = useConsumerSelector()
  const [method, setMethod] = useState<VerificationMethod | undefined>(initialMethod)
  const [codeEntryReset, setCodeEntryReset] = useState(0)
  const [codeEntryErrorMessage, setCodeEntryErrorMessage] = useState('')
  const setToast = useToast()
  const [allowSend, { add: addToAllowSend, remove: removeFromAllowSend }] = useSet<VerificationMethod>([VerificationMethod.EMAIL])
  const [wasAutoSent, setWasAutoSent] = useState(false)
  const [disableCodeEntry, setDisableCodeEntry] = useState(false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { textSize, headingSize } = useCheckoutSizes()
  const country = useCountry()
  const { formatNational } = usePhoneNumber({ country })
  const codeEntryRef = useRef<HTMLInputElement | undefined>()
  const logLoginAttemptEvent = useLogLoginAttemptEvent({ is2fa: true })
  const profileCardProps = useProfileCardProps()
  const isVerifySmsDelayEnabled = useFlag(Features.VERIFY_SMS_DELAY, false)
  const isApplePaylater = useIsApplePaylater()
  const isAutoRememberMeStepUpLive = useIsAutoRememberMeStepUp()
  const isAutoRememberMeStepUpInFlight = useIsAutoRememberMeStepUpInFlight()
  const stepUpEligibility = useAutoRememberMeStepUpEligibility()
  const isAutoRememberMeStepUpPending = stepUpEligibility === 'deciding'
  // Latched once live for the rest of the visit: check2faCode.fulfilled clears the step-up state
  // while the success flow is still fetching the account, and the chrome must not morph back to
  // the legacy variant (re-exposing ProfileCard's "Not you?") during that window. 'undecided'
  // (eligibility wait expired, flags never came) also latches: the step-up chrome sources identity
  // from the 207 body alone, whereas the legacy chrome would render the possibly-stale store.
  const [isAutoRememberMeStepUp, setIsAutoRememberMeStepUp] = useState(isAutoRememberMeStepUpLive)
  if ((isAutoRememberMeStepUpLive || stepUpEligibility === 'undecided') && !isAutoRememberMeStepUp) {
    setIsAutoRememberMeStepUp(true)
  }
  // The 'undecided' adoption is provisional: when the identify lands late and rules remember-me
  // out, the step-up is still in flight (unlike the post-2FA clear above), and the consumer gets
  // back the legacy chrome their flags actually resolved to.
  if (isAutoRememberMeStepUp && !isAutoRememberMeStepUpLive && isAutoRememberMeStepUpInFlight && stepUpEligibility === 'decided') {
    setIsAutoRememberMeStepUp(false)
  }
  // On any non-trusted 207 step-up the 207 body is the only trusted identity — the store
  // email/mobile may be a rehydrated previous consumer's on this partially-authenticated path
  // (and the 207 never carries an email), so absent values fall through to the no-destination
  // copy variants. Keyed on the raw in-flight signal, not the flag-classified chrome latch: the
  // flag evaluation can describe the rehydrated consumer too, so a step-up the flags mislabel as
  // legacy (including a revert after a late identify) must still keep the store identity out.
  const isStepUpIdentityUntrusted = useIsStepUpIdentityUntrusted()
  const isStepUpIdentityOnly = isAutoRememberMeStepUp || isStepUpIdentityUntrusted
  const email = isStepUpIdentityOnly ? '' : storeEmail
  const consumerMobile = isStepUpIdentityOnly ? stepUpIdentity?.maskedMobile : storeMobile
  const [smsDelayMs, setSmsDelayMs] = useState(0)
  const [sendingCode, setSendingCode] = useState(autoSendCode)
  const [isSkipCodeVerification, setIsSkipCodeVerification] = useState(false)
  const [fetchingSmsDelay, setFetchingSmsDelay] = useState(false)
  const dispatch = useAppDispatch()
  const router = useAppRouter()

  const mobile = revealMobile ? formatNational(consumerMobile ?? '') : maskPhoneNumber(consumerMobile)

  const { waitingFor, verifyTimeout, startVerifyTimeoutTimer, killVerifyTimeoutTimer } = useVerifyTimeout()
  // While a 207 step-up's Auto Remember Me eligibility is unresolved (LD identify in flight), the
  // legacy chrome would render the session-persisted store identity — possibly a rehydrated
  // previous consumer's — so hold the loader instead of treating the window as a non-step-up.
  const showLoadingScreen = smsDelayMs > 0 || fetchingSmsDelay || sendingCode || isSkipCodeVerification || isAutoRememberMeStepUpPending

  // CASHDAP-3954 (R4): the welcome-back transient covers the step-up's initial auto-send for at
  // least STEP_UP_WELCOME_BACK_MIN_MS before the OTP form. It needs a greeting name — an older
  // backend answers 207 without a body, and that step-up goes straight to the form (dwell starts
  // done). It may start late: the deciding-state loader keeps the form unmounted while the LD
  // identify is in flight, so eligibility's first positive decision can still lead with the
  // transient — but never once the form has been committed (a mid-session flag flip must not
  // flash it over visible code entry).
  const hasStepUpGreetingName = (stepUpIdentity?.givenNames?.trim() ?? '') !== ''
  const [stepUpWelcomeBack, setStepUpWelcomeBack] = useState<'unstarted' | 'showing' | 'done'>(
    isAutoRememberMeStepUp && hasStepUpGreetingName ? 'showing' : 'unstarted'
  )
  const hasCodeFormRendered = useRef(false)
  if (stepUpWelcomeBack === 'unstarted' && isAutoRememberMeStepUp && hasStepUpGreetingName && !hasCodeFormRendered.current) {
    setStepUpWelcomeBack('showing')
  }
  const showStepUpWelcomeBack = stepUpWelcomeBack === 'showing' && isAutoRememberMeStepUp
  useEffect(() => {
    if (stepUpWelcomeBack === 'showing') {
      const timer = setTimeout(() => setStepUpWelcomeBack('done'), STEP_UP_WELCOME_BACK_MIN_MS)
      return () => clearTimeout(timer)
    }
  }, [stepUpWelcomeBack])

  const onCodeComplete = async (code: string): Promise<void> => {
    if (method === undefined) {
      return
    }

    onComplete()
    setDisableCodeEntry(true)

    const loginMethod = TrackingEvent.LOGIN_WEB_PASSWORD as LoginMethodTypes
    try {
      setCodeEntryErrorMessage('')
      const checkCodeResult = await checkCode(code, method)
      onSuccess(checkCodeResult)
      if (context === VerificationFlowContext.Efa) {
        logEvent(TrackingEvent.EFA_VERIFICATION_SUCCESS)
      } else {
        logLoginAttemptEvent(loginMethod, true)
      }
    } catch (e) {
      handleVerificationError({ e, logEvent, onTerminalError, onError })
      // eslint-disable-next-line @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context
      const codeEntryErrorMessage = t(getErrorMessage(e), { context })
      setCodeEntryErrorMessage(codeEntryErrorMessage)
      resetDisabledState()
      setDisableCodeEntry(false)
      setCodeEntryReset(codeEntryReset + 1)
      if (context !== VerificationFlowContext.Efa) {
        logLoginAttemptEvent(loginMethod, false)
      }
    }
  }
  const sendSmsCodeSuccess = async (result?: CodeVerificationDirective | void): Promise<void> => {
    setSendingCode(false)

    // There are times we want to skip showing a code sent toast
    // For example, skipping sms or email verification
    if (result !== null && result !== undefined && result.skipCodeVerification) {
      setIsSkipCodeVerification(true)
      // short circuit into check code if skipping
      await onCodeComplete('111111')
      return
    }

    setCodeEntryReset(codeEntryReset + 1)
    setSmsDelayMs(0)
    setFetchingSmsDelay(false)

    setToast({
      message: t('verify:codeSent'),
      kind: 'Success',
      onShow: () => logEvent(TrackingEvent.VIEWED_VERIFY_SENT_TOAST, { mobile: maskPhoneNumber(mobile) }),
    })
  }

  useEffect(() => {
    if (verifyTimeout) {
      handleVerifyTimeOutError()
    }
  }, [verifyTimeout])

  const failedToSendCodeToastMessage = (e: VerificationError, method: VerificationMethod): ReactNode => {
    const key = getErrorToastTransKey(e, context, method ?? VerificationMethod.SMS)
    return (
      <Text>
        <Trans
          i18nKey={key}
          components={{
            mobile: <TransMaskedText actionName='Verify Code Mobile Number' text={mobile} testNameSpace='toast-verify-code-sent-fail-mobile' />,
            email: <TransMaskedText actionName='Verify Code Email' text={email ?? ''} testNameSpace='toast-verify-code-sent-fail-email' />,
          }}
        />
      </Text>
    )
  }

  const sendSmsCodeEventSuccess = async (e: FetchVerificationSuccessResponse): Promise<void> => {
    killVerifyTimeoutTimer()
    dispatch(verifyRegistrationMobileEventSuccess())
    await sendSmsCodeSuccess(e)
  }

  const sendSmsCodeEventError = (e: FetchVerificationErrorResponse): void => {
    killVerifyTimeoutTimer()
    try {
      const error = convertAuthzErrorEventToVerificationError(e)
      handleSendCodeError(VerificationMethod.SMS, error)
      dispatch(verifyRegistrationMobileEventError({ errorCode: e.message?.toLowerCase() as ErrorCode }))
    } catch (e) {
      onTerminalError()
    }
  }

  const sendSmsCodeEventWaiting = (d: FetchVerificationWaitResponse): void => {
    killVerifyTimeoutTimer()
    const delayMs = d.delayMs ?? 0
    setSendingCode(true)
    setSmsDelayMs(delayMs)
    setFetchingSmsDelay(false)
    startVerifyTimeoutTimer(WaitingForEvents.SUCCESS, delayMs)
    logEvent(TrackingEvent.TRIGGERED_SMS_VERIFY_TIME_DELAY, {
      waitTime: `${delayMs / 1000} s`,
    })
  }

  const handleVerifyTimeOutError = (): void => {
    setToast({
      message: t('verify:error:timeout'),
      kind: 'Failure',
      onShow: () =>
        logEvent(TrackingEvent.VIEWED_VERIFY_PAGE_TIMEOUT_TOAST, {
          waitingFor,
        }),
    })
    return onError()
  }

  const handleSendCodeError = (method, e): void => {
    setSendingCode(false)

    if (VerificationError.is(e)) {
      if (e.isFatal) {
        return onTerminalError(e.code)
      }

      if (e.isSignUpError) {
        if (
          e.code === VerificationErrorCode.ExistingPhoneNumber ||
          e.code === VerificationErrorCode.ExistingPhoneNumberSendEmail ||
          e.code === VerificationErrorCode.ExistingPhoneNumberUnicorn
        ) {
          logEvent(TrackingEvent.VIEWED_EXISTING_MOBILE_NUMBER_ERROR)
        }
        return onError(e.code)
      }
      if (e.isEfaNotRequired) {
        dispatch(resetCheckoutError())
        router.push(Route.SUMMARY)
      }
    }
    setToast({
      message: () => failedToSendCodeToastMessage(e, method),
      kind: 'Failure',
      onShow: () => logEvent(TrackingEvent.VIEWED_VERIFY_FAIL_TOAST, { mobile: maskPhoneNumber(mobile) }),
    })

    if (context === VerificationFlowContext.Efa) {
      addToAllowSend([VerificationMethod.SMS])
    } else {
      return onError()
    }
  }

  const sendBy = async (sendBy: VerificationMethod, disableAll: boolean = true): Promise<void> => {
    const triggerVerifyCallAuthz = sendSmsAuthorization !== undefined
    const triggerVerifySmsDelay = isVerifySmsDelayEnabled && sendBy === 'SMS' && sendSmsCodeEvent !== undefined
    try {
      setCodeEntryErrorMessage('')
      setMethod(sendBy)
      removeFromAllowSend(disableAll ? Object.values(VerificationMethod) : [sendBy])
      // TODO clean FF code once it's fully roll out. https://apglobal.atlassian.net/browse/CHECKOUT-3496
      if (triggerVerifyCallAuthz) {
        setFetchingSmsDelay(true)
        const result = await sendSmsAuthorization()
        if (result.code === VerifyRegistrationSMSAuthorizationCode.FAILED) {
          logEvent(TrackingEvent.VIEWED_VERIFY_PAGE_FAIL_TOAST, {
            errorCode: result.code,
            errorMessage: result.message,
          })
          return onTerminalError()
        }
        // TODO remove this logic once sms delay is enabled
        if (!triggerVerifySmsDelay) {
          setFetchingSmsDelay(false)
        }
      }
      if (triggerVerifySmsDelay) {
        await sendSmsCodeEvent({
          onSuccess: sendSmsCodeEventSuccess,
          onError: sendSmsCodeEventError,
          onWaiting: sendSmsCodeEventWaiting,
        })
        startVerifyTimeoutTimer(WaitingForEvents.WAIT)
      } else {
        setSendingCode(true)
        const sendCodeResult = await sendCode(sendBy)
        sendSmsCodeSuccess(sendCodeResult)
      }
    } catch (e) {
      handleSendCodeError(sendBy, e)
    }
  }

  const onClick = (m: VerificationMethod) => () => {
    logEvent(TrackingEvent[trackingEvents[m]])
    setWasAutoSent(false)
    sendBy(m)
  }

  useEffect(() => {
    if (!enabled.SMS && !enabled.EMAIL) {
      return onTerminalError(VerificationErrorCode.MethodNotEnabled)
    }

    if (autoSendCode) {
      setWasAutoSent(true)
      if (enabled.SMS) {
        sendBy(VerificationMethod.SMS, false)
      } else if (enabled.EMAIL) {
        sendBy(VerificationMethod.EMAIL, false)
      }
    }
  }, [])

  useEffect(() => {
    const input = codeEntryRef.current
    let focusTimeout
    if (input != null) {
      focusTimeout = setTimeout(() => input.focus(), 250)
    }
    return () => clearTimeout(focusTimeout)
    // The ref alone cannot reschedule this (assigning a ref does not render), so also re-run when
    // a gate that kept the input unmounted — the loading screen or the step-up welcome-back
    // transient — clears, or the field would never receive focus after those screens.
  }, [codeEntryRef.current, showLoadingScreen, showStepUpWelcomeBack])

  useEffect(() => {
    if (!showLoadingScreen && !showStepUpWelcomeBack) {
      hasCodeFormRendered.current = true
    }
  })

  useEffect(() => {
    if (allowSend.size < Object.keys(VerificationMethod).length) {
      const t = setTimeout(() => addToAllowSend(Object.values(VerificationMethod)), SEND_TIMEOUT_MS)
      return () => clearTimeout(t)
    }
  }, [allowSend])

  const chooseMethod = (): ReactNode => {
    const renderOption = (m: VerificationMethod): ReactNode =>
      enabled[m] ? (
        <Option
          onClick={onClick(m)}
          icon={icons[m]}
          testNameSpace={`choose-${m.toLowerCase()}`}
          text={t(`verify:${m.toLowerCase()}.sendOption`, { mobile, email })}
        />
      ) : null

    return (
      <>
        <Grid>
          <Text>{t('verify:chooseDescription')}</Text>
        </Grid>
        <Grid>
          <Divider kind='Dashed' />
        </Grid>
        <Grid>
          <Heading size='S'>{t('verify:chooseMethod')}</Heading>
        </Grid>
        <Grid>
          <div>
            {renderOption(VerificationMethod.SMS)}
            {renderOption(VerificationMethod.EMAIL)}
          </div>
        </Grid>
      </>
    )
  }

  const isRevealMobile = method === VerificationMethod.SMS && revealMobile

  const enterCode = (): ReactElement => {
    const SendCodeLink = (m: VerificationMethod): FunctionComponent => {
      const component: FunctionComponent<SendCodeLinkProps> = ({ children }) =>
        enabled[m] ? (
          <Button.Link size='Regular' disabled={disabled || !allowSend.has(m)} onClick={onClick(m)}>
            {children}
          </Button.Link>
        ) : null
      component.displayName = `Send${m}Link`
      return component
    }
    const SendSMS = SendCodeLink(VerificationMethod.SMS)
    const SendEmail = SendCodeLink(VerificationMethod.EMAIL)
    const components = { sms: <SendSMS />, email: <SendEmail /> }
    const isOnlyMethod = !Object.keys(VerificationMethod)
      .filter((k) => k !== method)
      .some((k) => enabled[k])

    return (
      <>
        <Description method={method} isRevealMobile={isRevealMobile} mobile={mobile} email={email} wasAutoSent={wasAutoSent} context={context} />
        <Grid>
          <CodeEntry
            name='verify-code'
            aria-labelledby='verify-code'
            codeEntryLength={6}
            disabled={disabled || disableCodeEntry}
            error={codeEntryErrorMessage !== ''}
            errorText={codeEntryErrorMessage}
            key={codeEntryReset}
            onComplete={onCodeComplete}
            testNameSpace='verify'
            ref={codeEntryRef}
          />
        </Grid>
        <Grid>
          <div className={clsx(styles.resendLinkContainer, isAutoRememberMeStepUp && styles.resendLinkContainerStepUp)}>
            <Text size={textSize}>
              <Trans
                i18nKey={`verify:${method?.toLowerCase()}.${isAutoRememberMeStepUp ? 'sendLinkStepUp' : 'sendLink'}`}
                context={isOnlyMethod ? 'only' : ''}
                components={components}
              />
            </Text>
          </div>
        </Grid>
      </>
    )
  }

  if (showStepUpWelcomeBack) {
    return <WelcomeBackRecognition stepUpGivenNames={stepUpIdentity?.givenNames} />
  }

  return showLoadingScreen ? (
    // delayMs 0 while eligibility is pending, and no ProfileCard on the step-up chrome: Verifying's
    // countdown branch renders ProfileCard from the store, which the step-up must never show.
    <Verifying
      allowLogout={allowLogout}
      isRevealMobile={isRevealMobile}
      delayMs={isAutoRememberMeStepUpPending ? 0 : smsDelayMs}
      isStepUpChrome={isStepUpIdentityOnly}
    />
  ) : (
    <>
      {previousRoute !== undefined && <BackLink backTo={previousRoute} disabled={disabled} hideText={isAutoRememberMeStepUp} />}
      {!isApplePaylater &&
        (isAutoRememberMeStepUp ? (
          <>
            <Grid>
              {/* Wrapped so Grid's stretched flex item is the div, not the svg — a stretched svg
                  re-centers its glyph (preserveAspectRatio) instead of staying left-aligned. */}
              <div>
                <StepUpBadge />
              </div>
            </Grid>
            <Grid>
              {/* The R4 heading names the delivery channel, so it's keyed by method, not flow context. */}
              <Heading size={headingSize}>
                {method === VerificationMethod.EMAIL ? t('verify:headingStepUp', { context: 'email' }) : t('verify:headingStepUp')}
              </Heading>
            </Grid>
          </>
        ) : (
          <Grid>
            {/* eslint-disable-next-line @afterpay/i18n-no-dynamic-context */}
            <Heading size={headingSize}>{t('verify:heading', { context })}</Heading>
          </Grid>
        ))}
      <FormWrapper>
        {/* Hidden on any in-flight 207, not just the step-up chrome — the card renders the store
            identity, which this partially-authenticated path cannot vouch for. */}
        {!isStepUpIdentityOnly && <ProfileCard allowLogout={allowLogout} revealMobile={isRevealMobile} {...profileCardProps} />}
        {method === undefined ? chooseMethod() : enterCode()}
      </FormWrapper>
    </>
  )
}
